import { io } from 'socket.io-client';

const CONNECT_TIMEOUT_MS = 10000;
const EVENT_RESPONSE_TIMEOUT_MS = 8000;

function apiBaseUrl() {
  return String(process.env.REACT_APP_BASE_URL || process.env.REACT_APP_API_BASE_URL || '').replace(
    /\/$/,
    ''
  );
}

function socketBaseUrl() {
  return String(process.env.REACT_APP_BASE_URL_WS || '').replace(/\/$/, '');
}

function errorMessage(error, fallback) {
  if (typeof error === 'string' && error.trim()) return error.trim();
  if (error && typeof error.message === 'string' && error.message.trim()) return error.message.trim();
  return fallback;
}

async function resolveSocialCollageTemplateId() {
  const fromEnv = String(process.env.REACT_APP_SOCIAL_COLLAGE_TEMPLATE_ID || '').trim();
  if (fromEnv) return fromEnv;

  const base = apiBaseUrl();
  if (!base) throw new Error('Missing API base URL.');

  const response = await fetch(`${base}/collage-templates/active?type=social`);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.message || 'Could not load the social collage.');
  }

  const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
  const id = list[0]?.id;
  if (!id) {
    throw new Error('No active social collage is set up on the wall yet.');
  }
  return String(id);
}

function normalizeOrientation(value) {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'landscape' ? 'landscape' : 'portrait';
}

export async function emitSocialCollage({ ticketId, imageUrl, text, username, orientation }) {
  const trimmedTicketId = String(ticketId || '').trim();
  const trimmedImage = String(imageUrl || '').trim();
  const trimmedText = String(text || '').trim();
  const trimmedUsername = String(username || '').trim();
  const imageOrientation = normalizeOrientation(orientation);

  if (!trimmedTicketId) throw new Error('Scan a ticket before adding to the wall.');
  if (!trimmedText) throw new Error('Message is required.');

  const collageTemplateId = await resolveSocialCollageTemplateId();
  const base = socketBaseUrl();
  if (!base) throw new Error('Live collage is unavailable right now.');

  const payload = {
    collageType: 'social',
    ticketId: trimmedTicketId,
    collageTemplateId,
    text: trimmedText,
    message: trimmedText,
    ...(trimmedImage
      ? {
          imageUrl: trimmedImage,
          image_url: trimmedImage,
          orientation: imageOrientation,
          image_orientation: imageOrientation,
        }
      : {}),
    ...(trimmedUsername ? { username: trimmedUsername } : {}),
  };

  await new Promise((resolve, reject) => {
    const socket = io(`${base}/ws/collage`, {
      transports: ['websocket'],
      reconnection: false,
      timeout: CONNECT_TIMEOUT_MS,
    });
    let settled = false;
    let connectTimer = null;
    let responseTimer = null;

    const finish = (error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(connectTimer);
      window.clearTimeout(responseTimer);
      socket.removeAllListeners();
      socket.disconnect();
      if (error) reject(error);
      else resolve();
    };

    connectTimer = window.setTimeout(() => {
      finish(new Error('Could not reach the wall. Please try again.'));
    }, CONNECT_TIMEOUT_MS);

    socket.on('connect', () => {
      window.clearTimeout(connectTimer);
      socket.emit('social-collage', payload);
      responseTimer = window.setTimeout(() => finish(), EVENT_RESPONSE_TIMEOUT_MS);
    });

    socket.on('socialCollageUpdated', () => finish());

    socket.on('error', (raw) => {
      finish(new Error(errorMessage(raw, 'Could not add your photo to the wall.')));
    });

    socket.on('connect_error', (raw) => {
      finish(new Error(errorMessage(raw, 'Could not reach the wall. Please try again.')));
    });
  });
}
